import React from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

// =============================================================================
// RouteErrorBoundary — a crashed page is a message, never a blank screen
// =============================================================================
// James, 6 Oct 2026: "groups in HSE alerts is just a blank page on live." The
// app had no error boundary, so any exception while rendering a route — a
// real bug, or a lazy chunk that no longer exists because a new build just
// deployed under an open tab — unmounted the whole tree and left a blank
// page with nothing to go on.
//
// Now: the shell stays, the page shows what broke (the error text, so it can
// be pasted straight to Claude) and offers Reload and Home.
//
// A missing chunk after a deploy (the tab holds the old build, the new build
// renamed every file) is NOT a page error and James never wants to see it
// ("whatever this is I DO NOT LIKE IT", 6 Oct): that case shows nothing but a
// quiet "Updating…" and reloads itself. The only guard is against a tight
// loop — more than three reloads inside a minute and it gives up and shows
// the card, because then the build itself is broken. Clears its error when
// the route changes (resetKey = pathname from App.tsx) WITHOUT remounting the
// page tree — keying the boundary on the path would rebuild every screen on
// every navigation, which is not how the app behaved before.
// =============================================================================

type Props = { children: React.ReactNode; resetKey?: string };
type State = { error: Error | null };

const CHUNK_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /ChunkLoadError/i,
  /Loading chunk .* failed/i,
  /error loading dynamically imported module/i,
];

function isChunkError(error: Error | null): boolean {
  return CHUNK_PATTERNS.some(p => p.test(String(error?.message ?? error ?? "")));
}

// True when this tab has already reloaded three times in the last minute.
function reloadStorm(): boolean {
  try {
    const now = Date.now();
    const recent = (JSON.parse(sessionStorage.getItem("coretel.chunk-reloads") ?? "[]") as number[]).filter(t => now - t < 60_000);
    return recent.length >= 3;
  } catch { return false; }
}

export default class RouteErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  componentDidCatch(error: Error) {
    // A stale tab after a deploy: the chunk it wants is gone. Reload — quietly.
    // Guard: more than three reloads inside a minute means the build itself is
    // broken, so stop and show the card instead of spinning.
    if (isChunkError(error) && !reloadStorm()) {
      try {
        const key = "coretel.chunk-reloads";
        const now = Date.now();
        const recent = (JSON.parse(sessionStorage.getItem(key) ?? "[]") as number[]).filter(t => now - t < 60_000);
        sessionStorage.setItem(key, JSON.stringify([...recent, now]));
      } catch { /* storage unavailable — reload anyway */ }
      window.location.reload();
      return;
    }
    // eslint-disable-next-line no-console
    console.error("[route error]", error);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const isChunk = isChunkError(error);
    if (isChunk && !reloadStorm()) {
      // reloading — show nothing alarming
      return (
        <div className="min-h-[40vh] flex items-center justify-center text-[12px] text-text-muted">
          <RefreshCw size={14} className="animate-spin mr-2"/> Updating Ops…
        </div>
      );
    }
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="rounded-lg border border-danger/40 bg-danger/[0.04] p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="text-danger shrink-0 mt-0.5"/>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-semibold text-text">
                {isChunk ? "Ops could not finish updating" : "This page hit an error"}
              </div>
              <div className="text-[12px] text-text-muted mt-1">
                {isChunk
                  ? "It tried to pick up the new build a few times and could not. Reload once more; if this stays, tell Claude."
                  : "The rest of Ops is fine. Reload the page; if it happens again, copy the line below to Claude."}
              </div>
              <pre className="mt-3 text-[11px] font-mono text-danger whitespace-pre-wrap break-words bg-ink-card border border-line rounded p-2 max-h-40 overflow-auto">
                {String(error.message ?? error)}
              </pre>
              <div className="mt-3 flex gap-2 flex-wrap">
                <button type="button" onClick={() => window.location.reload()}
                  className="inline-flex items-center gap-1.5 rounded-md bg-accent text-black px-3 py-1.5 text-[12px] font-medium hover:bg-accent-hover">
                  <RefreshCw size={13}/> Reload
                </button>
                <button type="button" onClick={() => { window.location.href = "/"; }}
                  className="inline-flex items-center gap-1.5 rounded-md border border-line bg-ink-card px-3 py-1.5 text-[12px] font-medium text-text hover:border-accent">
                  <Home size={13}/> Home
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
